import {z} from 'zod';
import type {Prisma} from '@wayra/database';

/**
 * Zod schema for cycling discipline types. Shared between web and email
 * packages via @wayra/helpers.
 */
export const Discipline = z.enum([
  'road',
  'gravel',
  'mountain',
  'crit',
  'cyclocross',
  'track',
  'triathlon',
  'randonneuring',
  'ultra',
  'other',
]);

export type Discipline = z.infer<typeof Discipline>;

/**
 * Zod schema for event type. Shared between web and email packages via
 * @wayra/helpers.
 */
export const EventType = z.enum(['route', 'race', 'ride']);

export type EventType = z.infer<typeof EventType>;

/**
 * Tag-membership criterion. Either `includesAll` (strict AND across the
 * given tag ids) or `includesAny` (OR across the given tag ids), or both.
 * At least one must be present and non-empty — that invariant is enforced
 * at the EventFilter level (z.discriminatedUnion can't accept refined
 * members).
 */
export const EventFilterTagsField = z.object({
  field: z.literal('tags'),
  includesAll: z.array(z.string().uuid()).optional(),
  includesAny: z.array(z.string().uuid()).optional(),
});

/**
 * Organizer-membership criterion. Matches events whose organizer is one of
 * the given organizer ids. At least one id must be present.
 */
export const EventFilterOrganizerField = z.object({
  field: z.literal('organizer'),
  organizerIds: z.array(z.string().uuid()).min(1),
});

/**
 * Discipline-membership criterion. Matches events whose discipline is
 * one of the given values. At least one must be present.
 */
export const EventFilterDisciplineField = z.object({
  field: z.literal('discipline'),
  disciplines: z.array(Discipline).min(1),
});

/**
 * Event-type criterion. Matches events whose eventType is one of the given
 * values. At least one must be present.
 */
export const EventFilterEventTypeField = z.object({
  field: z.literal('eventType'),
  eventTypes: z.array(EventType).min(1),
});

/**
 * Geo-radius criterion. Matches events within a bounding box approximating
 * a circle of `km` kilometres around the given `lat`/`lng` centre point.
 * `label` is the human-readable place name kept for re-display in the UI.
 */
export const EventFilterLocationField = z.object({
  field: z.literal('location'),
  lat: z.number().finite().min(-90).max(90),
  lng: z.number().finite().min(-180).max(180),
  km: z.number().min(5).max(500),
  label: z.string().min(1),
});

/**
 * Governing-body criterion. Matches events whose governingBody is one
 * of the given values. Values are free-form strings pulled from existing
 * event data (e.g. "USAC", "USAT", "RUSA").
 */
export const EventFilterGoverningBodyField = z.object({
  field: z.literal('governingBody'),
  values: z.array(z.string()).min(1),
});

/**
 * Discriminated union of all filter fields. Each subsequent slice of the
 * Advanced Event Filters project adds a new member here.
 */
export const EventFilterField = z.discriminatedUnion('field', [
  EventFilterTagsField,
  EventFilterOrganizerField,
  EventFilterDisciplineField,
  EventFilterEventTypeField,
  EventFilterLocationField,
  EventFilterGoverningBodyField,
]);

export type EventFilterField = z.infer<typeof EventFilterField>;

/**
 * Top-level filter contract stored in `EventList.filter` and accepted by
 * the smart-list server fns. Wrapped in `{ criteria: [...] }` to leave
 * room for a future `groupOperator` / nested groups without a storage
 * migration. superRefine enforces field-specific invariants the
 * discriminated union itself can't.
 */
export const EventFilter = z
  .object({
    criteria: z.array(EventFilterField).min(1),
  })
  .superRefine((data, ctx) => {
    data.criteria.forEach((c, i) => {
      if (c.field === 'tags') {
        const hasAll = (c.includesAll?.length ?? 0) > 0;
        const hasAny = (c.includesAny?.length ?? 0) > 0;
        if (!hasAll && !hasAny) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['criteria', i],
            message:
              'tags criterion must specify includesAll or includesAny',
          });
        }
      }
    });
  });

export type EventFilter = z.infer<typeof EventFilter>;

/**
 * Predicate used by the builder UI to enable the Save button and gate
 * the live-preview query. Re-uses the canonical Zod schema instead of
 * re-implementing the validity rules.
 */
export function isValidEventFilter(draft: unknown): draft is EventFilter {
  return EventFilter.safeParse(draft).success;
}

/**
 * Map from tag ID to tag name, used to resolve tag filters by name
 * instead of by ID so that duplicate tags across organizers all match.
 */
export type TagNameMap = ReadonlyMap<string, string>;

/**
 * Translates a single criterion into a Prisma `EventWhereInput` clause.
 * The discriminated union on `field` makes the exhaustive switch
 * type-safe — adding a new field to the union forces a new case here.
 *
 * When `tagNames` is provided, tag criteria match by case-insensitive
 * tag name (via the EventTag relation) instead of by tag ID. This
 * ensures that duplicate tags with the same label across organizers
 * all match.
 */
export function fieldToWhere(
  field: EventFilterField,
  tagNames?: TagNameMap,
): Prisma.EventWhereInput {
  switch (field.field) {
    case 'tags': {
      const clauses: Prisma.EventWhereInput[] = [];
      if (field.includesAll && field.includesAll.length > 0) {
        for (const tagId of field.includesAll) {
          const name = tagNames?.get(tagId);
          if (name) {
            clauses.push({
              tags: {some: {tag: {name: {equals: name, mode: 'insensitive'}}}},
            });
          } else {
            clauses.push({tags: {some: {tagId}}});
          }
        }
      }
      if (field.includesAny && field.includesAny.length > 0) {
        const names = field.includesAny
          .map((id) => tagNames?.get(id))
          .filter((n): n is string => n != null);
        if (names.length > 0) {
          const uniqueNames = [...new Set(
            names.map((n) => n.toLowerCase()),
          )];
          clauses.push({
            tags: {
              some: {tag: {name: {in: uniqueNames, mode: 'insensitive'}}},
            },
          });
        } else {
          clauses.push({tags: {some: {tagId: {in: field.includesAny}}}});
        }
      }
      if (clauses.length === 1) return clauses[0];
      return {AND: clauses};
    }
    case 'organizer':
      return {organizerId: {in: field.organizerIds}};
    case 'discipline':
      return {discipline: {in: field.disciplines}};
    case 'eventType':
      return {eventType: {in: field.eventTypes}};
    case 'location': {
      const latDelta = field.km / 111.32;
      const cosLat = Math.max(Math.cos((field.lat * Math.PI) / 180), 0.01);
      const lngDelta = field.km / (111.32 * cosLat);
      return {
        AND: [
          {lat: {gte: field.lat - latDelta, lte: field.lat + latDelta}},
          {lng: {gte: field.lng - lngDelta, lte: field.lng + lngDelta}},
        ],
      };
    }
    case 'governingBody':
      return {governingBody: {in: field.values}};
    default: {
      const exhaustive: never = field;
      throw new Error(`unknown filter field: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/**
 * Translates an `EventFilter` into a Prisma `EventWhereInput`. Multiple
 * criteria are ANDed together; OR / nested groups are not supported in
 * this slice. Always includes `archived: false` so no caller can
 * accidentally surface archived events through the smart-list pipe.
 */
export function eventFilterToPrismaWhere(
  filter: EventFilter,
  tagNames?: TagNameMap,
): Prisma.EventWhereInput {
  const criteriaClauses = filter.criteria.map((c) =>
    fieldToWhere(c, tagNames),
  );
  return {AND: [{archived: false}, ...criteriaClauses]};
}

/**
 * Returns the same `archived/endDate/startDate` predicate the manual-list
 * path uses (`futureEventFilter` in `server/savedEvent.ts`). Shared here
 * so both the web smart-list path and email digest Lambda use identical
 * filtering logic.
 */
export function futureEventPredicate(now: Date): Prisma.EventWhereInput {
  return {
    archived: false,
    OR: [
      {endDate: {gte: now}},
      {endDate: null, startDate: {gte: now}},
      {endDate: null, startDate: null},
    ],
  };
}
